"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import * as api from "@/lib/api";
import {
  clearSession,
  getAccessToken,
  getStoredAdmin,
  isSuperAdmin,
  persistSession,
} from "@/lib/auth-storage";
import type { AdminUser } from "@/lib/types";
import { navItems } from "@/lib/nav-items";

interface AuthContextValue {
  admin: AdminUser | null;
  loading: boolean;
  isSuperAdmin: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const [admin, setAdmin] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);

  const bootstrap = useCallback(async () => {
    const token = getAccessToken();
    const cached = getStoredAdmin();
    if (!token) {
      setAdmin(null);
      setLoading(false);
      return;
    }
    setAdmin(cached);
    try {
      const me = await api.fetchMe();
      setAdmin(me);
      localStorage.setItem("sporush_admin_user", JSON.stringify(me));
    } catch {
      clearSession();
      setAdmin(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  const login = useCallback(
    async (email: string, password: string) => {
      const res = await api.login(email, password);
      persistSession(
        res.tokens.accessToken,
        res.tokens.refreshToken,
        res.admin,
        res.tokens.expiresIn,
      );
      setAdmin(res.admin);
      const firstAllowed = navItems.find((item) =>
        (item.roles as readonly string[]).includes(res.admin.role),
      )?.href;
      router.replace(firstAllowed ?? "/dashboard");
    },
    [router],
  );

  const logout = useCallback(async () => {
    await api.logout(false);
    setAdmin(null);
    router.replace("/login");
  }, [router]);

  const refreshProfile = useCallback(async () => {
    const me = await api.fetchMe();
    setAdmin(me);
    localStorage.setItem("sporush_admin_user", JSON.stringify(me));
  }, []);

  const value = useMemo(
    () => ({
      admin,
      loading,
      isSuperAdmin: isSuperAdmin(admin),
      login,
      logout,
      refreshProfile,
    }),
    [admin, loading, login, logout, refreshProfile],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
